<template>
  <admin-wrapper
    title-translation-path="admin.defaultProjectSettings"
    @save="save"
    :has-validation="false"
    :is-fetched="isFetched"
    :inputs="inputs"
    :is-custom-saving="isLoading"
  >
    <template #map.input>
      <v-col cols="4">
        <admin-settings-map :emit-view="true" @viewChanged="viewChanged"></admin-settings-map>
      </v-col>
    </template>
    <template #default-layers.input>
      <v-col cols="4">
        <default-project-settings-layers :layersAdded="defaultLayers" @layersAdded="layersAdded" />
      </v-col>
    </template>
    <template #additional-buttons>
      <v-col cols="auto">
        <base-button translation-path="button.resetDefaultProject" @click="handleResetDefaultProject"></base-button>
      </v-col>
    </template>
  </admin-wrapper>
</template>

<script>
import { call, get } from 'vuex-pathify';

import AdminWrapper from '@/components/AdminWrapper';
import AdminSettingsMap from '@/components/AdminSettingsMap';
import DefaultProjectSettingsLayers from '@/components/DefaultProjectSettingsLayers';

export default {
  name: 'DefaultProjectSettings',
  components: {
    AdminWrapper,
    AdminSettingsMap,
    DefaultProjectSettingsLayers,
  },
  computed: {
    isCacheLayersAdminEnabled: get('admin/settingsValues@cache_layer_module_enabled'),
    isCacheLayersSuperAdminEnabled: get('admin/additionalModules@CACHE_LAYER_MODULE.enabled'),
    isCacheLayersEnabled() {
      return this.isCacheLayersSuperAdminEnabled && this.isCacheLayersAdminEnabled;
    },
  },
  data: () => ({
    viewZoom: null,
    viewCenter: null,
    isFetched: false,
    defaultLayers: [],
    inputs: [
      {
        name: 'map',
        align: 'start',
        translationPath: 'admin.map',
        subtitle: 'admin.defaultProjectMapSubtitle',
      },
      {
        name: 'default-layers',
        align: 'baseline',
        translationPath: 'admin.defaultLayers',
      },
    ],
    isLoading: false,
  }),
  methods: {
    getLayersSchema: call('layers/getLayersSchema'),
    getCacheLayersMetadata: call('layers/getCacheLayersMetadata'),
    getDefaultProjectValues: call('admin/getDefaultProjectValues'),
    saveDefaultProjectValues: call('layers/editProject'),
    resetDefaultProject: call('admin/resetDefaultProject'),
    async save() {
      this.isLoading = true;
      const payload = {
        default: true,
        layers: this.defaultLayers.map(layer => {
          if (typeof layer.id === 'string' && layer.id?.includes('ugi-')) {
            delete layer.id;
          }
          return { ...layer };
        }),
        map_center: {
          coordinates: this.viewCenter,
          crs: {
            properties: {
              name: this.$_config.defaultEpsg,
            },
            type: 'name',
          },
          type: 'Point',
        },
        zoom: this.viewZoom,
      };
      try {
        await this.saveDefaultProjectValues({ body: payload, id: 'default' });
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('success.successProjectEdit'),
        });
        this.isLoading = false;
      } catch {
        this.isLoading = false;
      }
    },
    async handleResetDefaultProject() {
      const r = await this.resetDefaultProject();
      this.$store.set('map/SET_ZOOM!', r.data.data.zoom);
      this.$store.set('map/SET_CENTER!', r.data.data.map_center.coordinates);
      this.defaultLayers = r.data.data.layers;
      this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
        message: this.$i18n.t('success.successProjectReset'),
      });
    },
    viewChanged(view) {
      const { zoom, center } = view;
      this.viewCenter = center;
      this.viewZoom = zoom;
    },
    layersAdded(layers) {
      this.defaultLayers = [...layers];
    },
    async init() {
      try {
        const promises = [this.getDefaultProjectValues(), this.getLayersSchema()];
        if (this.isCacheLayersEnabled) {
          promises.push(this.getCacheLayersMetadata());
        }
        const r = await Promise.all(promises);
        this.$store.set('map/SET_ZOOM!', r[0].data.data.zoom);
        this.$store.set('map/SET_CENTER!', r[0].data.data.map_center.coordinates);
        this.defaultLayers = r[0].data.data.layers;
        this.isFetched = true;
      } catch (e) {
        console.log('~ e', e);
      }
    },
  },
  mounted() {
    this.init();
  },
};
</script>

<style scoped lang="scss">
.border {
  @include border(all, 2px);
  border-style: dashed;
}
::v-deep .v-image__image--cover {
  background-size: unset !important;
}
</style>
